/**
 * API 工具模块 - 封装所有 HTTP 请求，自动添加认证 token
 */

import * as C from './constants';

// Token 存储 key（与 AuthContext 保持一致）
const TOKEN_KEY = 'cineart_token';
const SESSION_MODE_KEY = 'cineart_session_mode';

/**
 * 获取当前 token（自动感知会话模式）
 * 管理员"登录为其他用户"时 token 存在 sessionStorage 中
 */
export function getToken() {
  if (sessionStorage.getItem(SESSION_MODE_KEY)) {
    return sessionStorage.getItem(TOKEN_KEY);
  }
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * 带认证的 fetch 请求
 * @param {string} url - 请求 URL
 * @param {object} options - fetch 选项
 * @returns {Promise<Response>}
 */
let _isHandling401 = false; // 防止多个并发 401 响应重复处理
export async function authFetch(url, options = {}) {
  const token = getToken();
  
  const headers = {
    ...options.headers,
  };
  
  // 如果有 token，添加 Authorization header
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // 如果 body 是普通对象（排除 FormData, Blob, ArrayBuffer, URLSearchParams 等），自动设置 Content-Type
  if (options.body && typeof options.body === 'object'
      && !(options.body instanceof FormData)
      && !(options.body instanceof Blob)
      && !(options.body instanceof ArrayBuffer)
      && !(options.body instanceof URLSearchParams)) {
    headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(options.body);
  }
  
  const response = await fetch(url, { ...options, headers });
  
  // 如果 401，可能需要重新登录（使用标志位防止多个并发请求重复处理）
  if (response.status === 401 && !_isHandling401) {
    _isHandling401 = true;
    try {
      // 检查是否账户过期
      let isExpired = false;
      try {
        const clone = response.clone();
        const body = await clone.json();
        if (body.code === 'ACCOUNT_EXPIRED') isExpired = true;
      } catch {}

      // 🆕 根据当前模式清除正确的 storage
      if (sessionStorage.getItem(SESSION_MODE_KEY)) {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem('cineart_user');
        sessionStorage.removeItem(SESSION_MODE_KEY);
      } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('cineart_user');
      }
      
      // 只在非登录页时跳转
      if (!window.location.pathname.includes('/login')) {
        if (isExpired) {
          alert('您的账户已过期，请联系管理员续期');
        }
        window.location.href = '/login';
      }
    } finally {
      // 🛡️ 重置标志位，确保用户重新登录后 token 再次过期时能正常处理
      _isHandling401 = false;
    }
  }
  
  return response;
}

/**
 * 🛡️ 统一响应检查：非 2xx 状态码时抛出错误
 */
async function checkResponse(res) {
  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const data = await res.json();
      errorMsg = data.error || data.message || errorMsg;
    } catch (e) {
      // 响应体不是 JSON
    }
    throw new Error(errorMsg);
  }
  return res.json();
}

/**
 * GET 请求
 */
export async function apiGet(endpoint) {
  const url = endpoint.startsWith('http') ? endpoint : `${C.API_BASE_URL}${endpoint}`;
  const res = await authFetch(url);
  return checkResponse(res);
}

/**
 * POST 请求
 */
export async function apiPost(endpoint, data) {
  const url = endpoint.startsWith('http') ? endpoint : `${C.API_BASE_URL}${endpoint}`;
  const res = await authFetch(url, {
    method: 'POST',
    body: data
  });
  return checkResponse(res);
}

/**
 * PUT 请求
 */
export async function apiPut(endpoint, data) {
  const url = endpoint.startsWith('http') ? endpoint : `${C.API_BASE_URL}${endpoint}`;
  const res = await authFetch(url, {
    method: 'PUT',
    body: data
  });
  return checkResponse(res);
}

/**
 * DELETE 请求
 */
export async function apiDelete(endpoint, data = null) {
  const url = endpoint.startsWith('http') ? endpoint : `${C.API_BASE_URL}${endpoint}`;
  const options = { method: 'DELETE' };
  if (data) {
    options.body = data;
  }
  const res = await authFetch(url, options);
  return checkResponse(res);
}

/**
 * PATCH 请求 🆕
 */
export async function apiPatch(endpoint, data = null) {
  const url = endpoint.startsWith('http') ? endpoint : `${C.API_BASE_URL}${endpoint}`;
  const options = { method: 'PATCH' };
  if (data) {
    options.body = data;
  }
  const res = await authFetch(url, options);
  return checkResponse(res);
}

/**
 * 上传文件（FormData）
 */
export async function apiUpload(endpoint, formData) {
  const url = endpoint.startsWith('http') ? endpoint : `${C.API_BASE_URL}${endpoint}`;
  const token = getToken();
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // 注意：不要设置 Content-Type，让浏览器自动设置 multipart/form-data
  
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: formData
  });
  return checkResponse(res);
}

/**
 * Presign 直传云存储（大文件专用：图片/视频/音频）
 * 流程：服务端生成层级化 key → 获取签名 URL → 浏览器直传云存储 → 通知服务器注册引用
 * @param {File} file - 要上传的文件
 * @param {string} prefix - 已弃用，保留兼容（实际由服务端生成 key）
 * @returns {Promise<{url: string, filename: string}>}
 */
export async function presignUpload(file, prefix = 'uploads/', { signal } = {}) {
  // 🛡️ 护栏 4：拒绝空文件 —— 最后一道兜底，防止前 3 道全失守时还能写脏数据进 OSS
  // 历史事故 (2026-06-06)：MJ vendor URL 缓存时拿到 0 字节，死链恢复路径把 0 字节
  // 当成"恢复成功"上传到自家 OSS，导致节点 output 永久指向 0 字节文件。
  if (!file || file.size === 0) {
    const error = new Error('拒绝上传 0 字节文件（请确认源数据完整）');
    error.code = 'EMPTY_FILE';
    throw error;
  }

  // 1. 推断文件类型和扩展名
  const fileType = file.type.startsWith('video/') ? 'video'
    : file.type.startsWith('audio/') ? 'audio' : 'image';
  const ext = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';

  // 2. 向服务端请求层级化云存储 key
  let key;
  try {
    const keyData = await apiGet(
      `/api/upload/generate-oss-key?fileType=${encodeURIComponent(fileType)}&ext=${encodeURIComponent(ext)}`
    );
    key = keyData.key;
  } catch (e) {
    // 服务端接口不可用时降级为旧格式
    console.warn('[presignUpload] generate-oss-key 失败，降级为旧 key 格式:', e.message || e);
    key = `${prefix}${Date.now()}-${Math.random().toString(36).substring(2, 8)}${ext}`;
  }

  // 3. 向服务器请求签名 URL
  const presignData = await apiGet(
    `/api/s3/presign?key=${encodeURIComponent(key)}&contentType=${encodeURIComponent(file.type)}`
  );

  // 🔧 检查云存储是否可用
  if (presignData.ossAvailable === false) {
    // 云存储未配置，抛出特殊错误让调用方降级到本地上传
    const error = new Error(presignData.message || '云存储未配置');
    error.code = 'OSS_UNAVAILABLE';  // 保持向后兼容
    throw error;
  }

  if (presignData.error) {
    throw new Error(presignData.error);
  }

  // 4. 浏览器直传云存储（大文件按网速动态超时）
  // 按 20KB/s 慢网估算：50MB ≈ 42分钟，最低 10 分钟
  const uploadTimeout = Math.max(10 * 60 * 1000, Math.ceil(file.size / 1024) * 50); // 至少10分钟，或按1KB/50ms估算
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), uploadTimeout);

  // 🆕 如果调用方传入了外部 signal（节点删除时触发），监听它来取消上传
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) { clearTimeout(timer); throw new DOMException('Upload aborted', 'AbortError'); }
    signal.addEventListener('abort', onExternalAbort);
  }

  let putRes;
  try {
    putRes = await fetch(presignData.presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 
        'Content-Type': file.type,
        // ⚠️ 不在此处手动添加 Content-Disposition：
        // 1. R2 预签名模式：该头不在签名列表内，多余传递会导致签名不匹配
        // 2. 云存储预签名已在服务端签名中包含，不需要前端重复
      },
      signal: controller.signal
    });
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }

  if (!putRes.ok) {
    throw new Error(`云存储上传失败: ${putRes.status} ${putRes.statusText}`);
  }

  // 5. 通知服务器注册文件引用
  try {
    await apiPost('/api/files/register-ref', {
      url: presignData.objectUrl,
      fileType,
      fileSize: file.size
    });
  } catch (e) {
    console.warn('注册文件引用失败（不影响上传）:', e);
  }

  // 6. 🆕 生成派生资源（缩略图 / 视频首帧海报 / 宽高体积）——自建化关键：
  //    上传图/视频也带上这些字段，前端画布不依赖 OSS 现算即可显示缩略图/海报/分辨率。
  //    仅对图片/视频尝试；失败/超时不影响上传结果（字段缺了前端会回退 OSS）。
  //    ⚠️ 跨域存储延迟优化：派生生成异步化，前端不阻塞等待，后台慢慢生成
  let derived = {};
  if (fileType === 'image' || fileType === 'video') {
    try {
      // 🔧 30秒超时：派生生成不阻塞上传完成。fetch+ffmpeg+sharp通常<5s，
      // 30s 是保守上限应对跨域网络波动（香港→华北 OSS）
      const derivePromise = Promise.race([
        apiPost('/api/upload/derive', { url: presignData.objectUrl, fileType }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('派生生成超时')), 30000)
        )
      ]);
      derived = await derivePromise || {};
    } catch (e) {
      console.warn('生成派生资源失败或超时（不影响上传）:', e.message);
      // 前端回退到 OSS 现算（如果没拿到 thumbUrl/posterUrl 等字段）
    }
  }

  return { url: presignData.objectUrl, filename: key, ...derived };
}

const api = {
  authFetch,
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch, // 🆕
  delete: apiDelete,
  upload: apiUpload,
  presignUpload,
  getToken
};

export default api;
